<template>
  <div>
    <SectionWrapper :class="editMode ? 'editmode' : 'viewmode'">
      <form :class="isDisabled('sfmPositionId') ? 'viewmode' : ''">
        <FormInputText
          v-model="areaDesignation"
          name="areaDesignation"
          :isDirty="handleDirtyState('areaDesignation')"
          :initialValue="editData?.areaDesignation"
          :disabled="isDisabled('areaDesignation')"
          :placeholder="ph('areaDesignation')"
          :placeholderSource="getPlaceholderSource('areaDesignation')"
          :suggestions="props.suggestions?.areaDesignation"
          :acceptsPlaceholderData="true"
        />
        <ResponsiveWrapper :itemCount="4">
          <FormInputText
            :key="`nameOnStatement-${editData?.id}`"
            v-model="nameOnStatement"
            name="nameOnStatement"
            :isDirty="handleDirtyState('nameOnStatement')"
            :initialValue="editData?.nameOnStatement"
            :disabled="isDisabled('nameOnStatement')"
            :placeholder="ph('nameOnStatement')"
            :placeholderSource="getPlaceholderSource('nameOnStatement')"
            :suggestions="props.suggestions?.nameOnStatement"
            :suggestionSources="props.suggestionSources?.nameOnStatement"
            :acceptsPlaceholderData="true"
          />
          <FormSelect
            v-model="sfmPositionId"
            name="sfmPositionId"
            label="sfmPosition"
            :options="sfmPositions"
            :filter="true"
            :isDirty="handleDirtyState('sfmPositionId')"
            :initialValue="editData?.sfmPositionId"
            :disabled="isDisabled('sfmPositionId')"
            :placeholder="ph('sfmPositionId')"
            :placeholderSource="getPlaceholderSource('sfmPositionId')"
            :acceptPlaceholderData="applyPlaceholderTrigger"
          />
        </ResponsiveWrapper>
        <ResponsiveWrapper :itemCount="2">
          <FormInputNumber
            v-model="totalArea"
            name="totalArea"
            :isDirty="handleDirtyState('totalArea')"
            formatType="squaremeter"
            :decimals="0"
            :alignRight="true"
            :initialValue="editData?.totalArea"
            :minFractionDigits="2"
            :suffix="' m²'"
            :disabled="isDisabled('totalArea')"
            :placeholder="ph('totalArea')"
            :placeholderSource="getPlaceholderSource('totalArea')"
            :acceptsPlaceholderData="true"
          />
          <FormInputNumber
            v-model="totalAmount"
            name="totalAmount"
            :isDirty="handleDirtyState('totalAmount')"
            :initialValue="editData?.totalAmount"
            formatType="currency"
            :currency="effectiveCurrency"
            :disabled="isDisabled('totalAmount')"
            :placeholder="ph('totalAmount')"
            :placeholderSource="getPlaceholderSource('totalAmount')"
            :acceptsPlaceholderData="true"
          />
        </ResponsiveWrapper>
        <ResponsiveWrapper :itemCount="2">
          <FormInputNumber
            v-model="customerArea"
            name="customerArea"
            :isDirty="handleDirtyState('customerArea')"
            :initialValue="editData?.customerArea"
            formatType="squaremeter"
            :decimals="0"
            :alignRight="true"
            :disabled="isDisabled('customerArea')"
            :placeholder="ph('customerArea') ?? 'm&sup2;'"
            :placeholderSource="getPlaceholderSource('customerArea')"
            :acceptsPlaceholderData="true"
            :minFractionDigits="2"
            :suffix="' m²'"
          />
          <FormInputNumber
            v-model="customerAmount"
            :labelTooltip="`${$t('labels.customerAmountSuggestion')}: ${customerAmountSuggestion} ${effectiveCurrency}`"
            name="customerAmount"
            :isDirty="handleDirtyState('customerAmount')"
            :initialValue="editData?.customerAmount"
            formatType="currency"
            :currency="effectiveCurrency"
            :disabled="isDisabled('customerAmount')"
            :placeholder="ph('customerAmount')"
            :placeholderSource="getPlaceholderSource('customerAmount')"
            :acceptsPlaceholderData="true"
          />
        </ResponsiveWrapper>

        <ResponsiveWrapper :itemCount="2">
          <FormCheckbox
            v-model="isFlatRate"
            name="isFlatRate"
            :isDirty="handleDirtyState('isFlatRate')"
            :initialValue="editData?.isFlatRate ?? false"
            :disabled="isDisabled('isFlatRate')"
            :placeholder="phBool('isFlatRate')"
            :placeholderSource="getPlaceholderSource('isFlatRate')"
            :acceptsPlaceholderData="true"
          />
          <FormSelect
            v-model="status"
            name="status"
            :options="Object.values(OperatingCostInvoiceLineItemV1Dto.status)"
            :isDirty="handleDirtyState('status')"
            :initialValue="editData?.status"
            :disabled="isDisabled('status')"
            :placeholder="ph('status')"
            :placeholderSource="getPlaceholderSource('status')"
            :acceptPlaceholderData="applyPlaceholderTrigger"
          />
        </ResponsiveWrapper>
        <DetailFormfooter
          :isNew="isCreate"
          :editMode="editMode"
          :isAnyFieldDirty="isAnyFieldDirty"
          :onGoToList="goToList"
          :onToggleEditMode="() => {}"
          :onSubmit="submitForm"
          :onConfirmResetChanges="confirmResetChanges"
          :alwaysShowSave="isCreate && !hasSuggestionsToApply"
          :onSaveAndCreateNext="submitFormForNextEntry"
        >
          <ApplyAllSuggestionsButton
            v-if="editMode && hasSuggestionsToApply"
            :onApplyAllSuggestions="applyAllSuggestionsAndSave"
            :suggestionsSource="suggestionSourceForApplyButton"
            :hasSuggestionsToApply="hasSuggestionsToApply"
            :buttonLabel="t('buttons.applyAllValuesAndSave')"
          />
        </DetailFormfooter>
      </form>
    </SectionWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import ApplyAllSuggestionsButton from '@/components/buttons/ApplyAllSuggestionsButton.vue';
import type {
  DetailFormDisableProps,
  DetailFormExtraProps,
} from '@/components/details/detailTypes';
import {
  DetailNavigationInlinePropsDefaults,
  useDetailNavigation,
  type UseDetailNavigationInlineProps,
} from '@/components/details/hooks/useDetailNavigation';
import type { SelectModel } from '@/components/forms/FormSelect.vue';
import { useFormDirtyState } from '@/components/forms/hooks/useFormDirtyState';
import { useFieldDisabling } from '@/components/forms/hooks/useFormFieldDisable';
import { useSfmPositions } from '@/views/sfmpositions/hooks/useSfmPositions';
import {
  CreateOperatingCostInvoiceLineItemV1Dto,
  OperatingCostInvoiceLineItemV1Dto,
} from '@/openapi';

import { getOperatingCostInvoiceLineSchema } from './configs/operatingCostInvoiceLineFormConfig';
import {
  useOperatingCostInvoiceLines,
  useOperatingCostInvoiceLinesConverter,
} from './hooks/useOperatingCostInvoiceLines';
import type {
  InvoiceItemSource,
  InvoiceItemSuggestionSource,
  OperatingCostInvoiceLineItemSimple,
} from './operatingCostInvoiceTypes';

export interface CostInvoiceLineFormProps {
  isNew: boolean;
  isCreate: boolean;
  formData: OperatingCostInvoiceLineItemSimple | undefined;
  currency?: string;
  suggestionSources?: Partial<
    Record<keyof OperatingCostInvoiceLineItemSimple, Array<'ai' | 'latest' | 'other'>>
  >;
  templateSource?: InvoiceItemSource;
  onAddCostInvoiceLineToCreate: (item: CreateOperatingCostInvoiceLineItemV1Dto) => void;
  onAddCostInvoiceLineToUpdate: (item: OperatingCostInvoiceLineItemSimple) => void;
  onModalClose: (reopenForNextEntry?: boolean) => void | Promise<void>;
  daysInBillingPeriod?: number;
}

const props = withDefaults(
  defineProps<
    UseDetailNavigationInlineProps &
      CostInvoiceLineFormProps &
      DetailFormExtraProps<OperatingCostInvoiceLineItemSimple> &
      DetailFormDisableProps<OperatingCostInvoiceLineItemSimple>
  >(),
  {
    ...DetailNavigationInlinePropsDefaults,
    initialData: undefined,
    placeholders: undefined,
    allDisabled: undefined,
    disabledFields: undefined,
    suggestions: undefined,
    suggestionSources: undefined,
    currency: 'EUR',
    templateSource: undefined,
    daysInBillingPeriod: undefined,
  },
);

const { t } = useI18n();

const { id, goToList, subComponentID } = useDetailNavigation({
  uuidFromProp: computed(() => props.uuid), // override id
  isNewFromProp: computed(() => props.isNew), // override isNew
  editModeFromProp: computed(() => props.editMode), // override editMode
});

const { isAnyFieldDirty, handleDirtyState } = useFormDirtyState();

const lineEditMode = computed(() => true); // Line form is always in edit mode inside modal

const { isDisabled } = useFieldDisabling<OperatingCostInvoiceLineItemSimple>({
  editMode: lineEditMode,
  allDisabled: computed(() => props.allDisabled),
  disabledFields: computed(() => props.disabledFields),
});

// Ensure currency always has a value (fallback to EUR if not provided)
const effectiveCurrency = computed(() => props.currency || 'EUR');

const { createOperatingCostInvoiceLine, updateOperatingCostInvoiceLine } =
  useOperatingCostInvoiceLines();
const {
  convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto,
  convertOperatingCostInvoiceLineItemSimpleToPatchOperatingCostInvoiceLineItemDto,
} = useOperatingCostInvoiceLinesConverter();
const { fetchSfmPositions } = useSfmPositions();

const editData = ref<OperatingCostInvoiceLineItemSimple>();
const sfmPositions = ref<SelectModel[]>([]);

const { handleSubmit, setValues } = useForm<OperatingCostInvoiceLineItemSimple>({
  validationSchema: getOperatingCostInvoiceLineSchema(t),
});
const submitForm = handleSubmit((values) => onSubmit(values, false));
const submitFormForNextEntry = handleSubmit((values) => onSubmit(values, true));

const { value: areaDesignation } = useField<string>('areaDesignation');
const { value: nameOnStatement } = useField<string>('nameOnStatement');
const { value: isFlatRate } = useField<boolean>('isFlatRate');
const { value: status } = useField<string>('status');
const { value: totalAmount } = useField<number | undefined>('totalAmount');
const { value: customerAmount } = useField<number | undefined>('customerAmount');
const { value: totalArea } = useField<number | undefined>('totalArea');
const { value: customerArea } = useField<number | undefined>('customerArea');
const { value: sfmPositionId } = useField<string>('sfmPositionId');
const applyPlaceholderTrigger = ref(0);

const customerAmountSuggestion = ref<number | undefined>(undefined);

function confirmResetChanges() {
  setValues({ ...props.formData });
}

function isFieldEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

function handleApplyAllSuggestions() {
  if (!props.placeholders) {
    return;
  }

  // Trigger FormSelect placeholder-accept flow for select fields.
  applyPlaceholderTrigger.value += 1;

  const fieldsToUpdate: Partial<OperatingCostInvoiceLineItemSimple> = {};

  const fieldValueMap: Partial<Record<keyof OperatingCostInvoiceLineItemSimple, any>> = {
    areaDesignation,
    nameOnStatement,
    sfmPositionId,
    totalArea,
    totalAmount,
    customerArea,
    customerAmount,
    status,
  };

  for (const [fieldName, fieldRef] of Object.entries(fieldValueMap)) {
    const key = fieldName as keyof OperatingCostInvoiceLineItemSimple;
    const placeholder = props.placeholders[key];
    if (placeholder === undefined || placeholder === null || placeholder === '') {
      continue;
    }

    const currentFieldValue = fieldRef?.value;
    if (!isFieldEmpty(currentFieldValue)) {
      continue;
    }

    if (
      key === 'totalArea' ||
      key === 'totalAmount' ||
      key === 'customerArea' ||
      key === 'customerAmount'
    ) {
      const parsed = Number(String(placeholder).replace(',', '.'));
      (fieldsToUpdate as any)[key] = Number.isFinite(parsed) ? parsed : placeholder;
      continue;
    }

    (fieldsToUpdate as any)[key] = placeholder;
  }

  const boolPlaceholder = phBool('isFlatRate', null);
  if ((isFlatRate.value === null || isFlatRate.value === undefined) && boolPlaceholder !== null) {
    fieldsToUpdate.isFlatRate = boolPlaceholder;
  }

  setValues(fieldsToUpdate);
}

const hasSuggestionsToApply = computed(() => {
  if (!props.placeholders) {
    return false;
  }

  const fieldValueMap: Partial<Record<keyof OperatingCostInvoiceLineItemSimple, any>> = {
    areaDesignation,
    nameOnStatement,
    sfmPositionId,
    totalArea,
    totalAmount,
    customerArea,
    customerAmount,
    status,
  };

  for (const [fieldName, fieldRef] of Object.entries(fieldValueMap)) {
    const key = fieldName as keyof OperatingCostInvoiceLineItemSimple;
    const placeholder = props.placeholders[key];
    if (placeholder === undefined || placeholder === null || placeholder === '') {
      continue;
    }
    if (isFieldEmpty(fieldRef?.value)) {
      return true;
    }
  }

  const boolPlaceholder = phBool('isFlatRate', null);
  if ((isFlatRate.value === null || isFlatRate.value === undefined) && boolPlaceholder !== null) {
    return true;
  }

  return false;
});

function applyAllSuggestionsAndSave() {
  handleApplyAllSuggestions();
  void submitForm();
}

async function onSubmit(
  values: OperatingCostInvoiceLineItemSimple,
  reopenForNextEntry: boolean = false,
) {
  if (props.isNew) {
    if (props.isCreate) {
      props.onAddCostInvoiceLineToCreate(
        convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto(values),
      );
    } else {
      props.onAddCostInvoiceLineToUpdate(values);
    }
  } else {
    if (!props.isCreate) {
      await updateOperatingCostInvoiceLine(
        String(props.uuid ? id.value : subComponentID.value),
        values.id,
        convertOperatingCostInvoiceLineItemSimpleToPatchOperatingCostInvoiceLineItemDto(values),
      );
    } else {
      await createOperatingCostInvoiceLine(
        String(props.uuid ? id.value : subComponentID.value),
        convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto(values),
      );
    }
  }

  await props.onModalClose(reopenForNextEntry);
}

// Placeholder helper for string fields
const ph = (key: keyof OperatingCostInvoiceLineItemSimple, fallback?: string) =>
  props.placeholders?.[key] ?? fallback ?? '';

// Placeholder helper for boolean fields (checkbox)
const phBool = (
  key: keyof OperatingCostInvoiceLineItemSimple,
  fallback?: boolean | null,
): boolean | null => {
  const value: string | boolean | null | undefined = props.placeholders?.[key];
  return `${value}` === 'true' ? true : `${value}` === 'false' ? false : (fallback ?? null);
};

// Compute placeholder source for any given field
const getPlaceholderSource = (fieldName: string): InvoiceItemSource | undefined => {
  if (!props.placeholders?.[fieldName as keyof OperatingCostInvoiceLineItemSimple]) {
    return undefined; // no placeholder → no source icon
  }
  return props.templateSource;
};

const suggestionSourceForApplyButton = computed<InvoiceItemSuggestionSource | undefined>(() => {
  if (
    props.templateSource === 'ai' ||
    props.templateSource === 'latest' ||
    props.templateSource === 'other'
  ) {
    return props.templateSource;
  }
  return undefined;
});

const fetchLookups = async () => {
  const positions = (await fetchSfmPositions()) ?? [];
  sfmPositions.value = positions
    .flatMap((parent) => {
      return (parent.children ?? []).map((child) => ({
        id: child.id,
        name: `${child.name} (${parent.name})`,
      }));
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

const calculateCustomerAmount = () => {
  if (
    totalAmount.value === undefined ||
    totalArea.value === undefined ||
    customerArea.value === undefined ||
    totalAmount.value <= 0 ||
    totalArea.value <= 0 ||
    !props.daysInBillingPeriod
  ) {
    return;
  }

  // Step 1: (Total Amount / Total Area) * Customer Area
  const customerAmountFromArea = (totalAmount.value / totalArea.value) * customerArea.value;

  // Step 2: (Customer Amount / 365) * Days in Billing Period

  const customerAmountPerYear =
    Math.round((customerAmountFromArea / 365) * props.daysInBillingPeriod * 100) / 100;

  customerAmountSuggestion.value = customerAmountPerYear;

  if (props.isCreate) {
    customerAmount.value = customerAmountPerYear;
  }
};

onMounted(async () => {
  await fetchLookups();

  if (props.initialData) {
    editData.value = {
      ...(props.formData ?? {}),
      ...props.initialData,
      status:
        props.formData?.status ||
        props.initialData.status ||
        OperatingCostInvoiceLineItemV1Dto.status.CLEAR,
    } as OperatingCostInvoiceLineItemSimple;
    setValues(editData.value);
    return;
  }

  // Edit mode path: when modal opens for an existing line, formData must prefill immediately.
  if (props.formData) {
    editData.value = props.formData;
    setValues({ ...props.formData });
  }
});

// Watch for changes in formData (e.g. when parent computed property updates)
watch(
  () => props.formData,
  (newFormData) => {
    if (newFormData && !props.initialData) {
      editData.value = newFormData;
      setValues({ ...newFormData });
    }
  },
  { deep: true, immediate: true },
);

// Watch for changes in totalArea, totalAmount, or customerArea to recalculate customer amount
watch([totalArea, totalAmount, customerArea], () => {
  calculateCustomerAmount();
});

// Watch for changes in days from parent
watch(
  () => props.daysInBillingPeriod,
  () => {
    calculateCustomerAmount();
  },
);
</script>
